/**
 * Config Registry - Single Source of Truth for ALL Configuration Keys
 *
 * This registry defines every configuration key used in the application with:
 * - Source: ConfigSource (env, setting, feature)
 * - Type: ConfigType (string, number, boolean)
 * - Metadata for validation and auto-seeding
 *
 * Provides compile-time type safety and runtime validation.
 */

export enum ConfigSource {
  Env = 'env',
  Setting = 'setting',
  Feature = 'feature',
}

export enum ConfigType {
  String = 'string',
  Number = 'number',
  Boolean = 'boolean',
  Color = 'color',
}

export enum SettingCategory {
  General = 'general',
  Features = 'features',
  Branding = 'branding',
  Email = 'email',
  Twilio = 'twilio',
  Telnyx = 'telnyx',
  Call = 'call',
  API = 'api',
  VoiceAgent = 'voice_agent',
  Payments = 'payments',
  DealerKit = 'dealer_kit',
  WhatsApp = 'whatsapp',
  Campaigns = 'campaigns',
  DigitalSign = 'digital_sign',
  Messaging = 'messaging',
  SalesConsole = 'sales_console',
  AgentImprovement = 'agent_improvement',
}

export type ConfigValue = string | number | boolean;

export interface BaseConfigEntry {
  source: ConfigSource;
  type: ConfigType;
}

export interface EnvConfigEntry extends BaseConfigEntry {
  source: ConfigSource.Env;
  required?: boolean;
  default?: ConfigValue;
}

export interface SettingConfigEntry extends BaseConfigEntry {
  source: ConfigSource.Setting;
  category: string;
  label: string;
  description: string;
  is_secret?: boolean;
  sort_order: number;
  default?: ConfigValue | null;
  frontend?: boolean;
}

export interface FeatureConfigEntry extends BaseConfigEntry {
  source: ConfigSource.Feature;
  default?: ConfigValue;
  frontend?: boolean;
}

export type ConfigEntry = EnvConfigEntry | SettingConfigEntry | FeatureConfigEntry;

/**
 * Complete Configuration Registry
 * Every key used in the application MUST be defined here
 */
export const CONFIG_REGISTRY = {
  // ==================== ENV-ONLY KEYS ====================
  // Database
  DB_HOST: { source: ConfigSource.Env, type: ConfigType.String, required: true },
  DB_PORT: { source: ConfigSource.Env, type: ConfigType.Number, required: true },
  DB_NAME: { source: ConfigSource.Env, type: ConfigType.String, required: true },
  DB_USER: { source: ConfigSource.Env, type: ConfigType.String, required: true },
  DB_PASSWORD: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    required: true,
  },

  // JWT
  JWT_SECRET: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    required: true,
  },
  JWT_EXPIRATION: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    default: '7d',
  },

  // Redis
  REDIS_URL: { source: ConfigSource.Env, type: ConfigType.String },
  REDIS_HOST: { source: ConfigSource.Env, type: ConfigType.String, default: 'localhost' },
  REDIS_PORT: { source: ConfigSource.Env, type: ConfigType.Number, default: 6379 },
  REDIS_PASSWORD: { source: ConfigSource.Env, type: ConfigType.String },

  // Server
  PORT: { source: ConfigSource.Env, type: ConfigType.Number, default: 3000 },
  NODE_ENV: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    default: 'development',
  },
  FRONTEND_URL: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    required: true,
  },
  CLIENT_COMPANY_NAME: {
    source: ConfigSource.Env,
    type: ConfigType.String,
  },
  CALLBACK_CLIENT_NAME: {
    source: ConfigSource.Env,
    type: ConfigType.String,
  },
  APP_URL: { source: ConfigSource.Env, type: ConfigType.String, required: true },
  NGROK_URL: { source: ConfigSource.Env, type: ConfigType.String },

  // Settings Encryption
  SETTINGS_ENCRYPTION_KEY: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    required: true,
  },

  // Admin Credentials
  ADMIN_NAME: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    required: true,
  },
  ADMIN_EMAIL: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    required: true,
  },
  ADMIN_PASSWORD: {
    source: ConfigSource.Env,
    type: ConfigType.String,
  },
  AUTH_CODE_EXPIRY_MINUTES: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.General,
    label: 'Login Code Expiry (minutes)',
    description: 'Number of minutes before a login verification code expires',
    sort_order: 1,
    default: 10,
  },
  BOOKING_TIMEZONE: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.General,
    label: 'Booking Timezone',
    description:
      'IANA timezone for bookings (e.g. Europe/London, America/Sao_Paulo). Workflow booking dates/times are interpreted as wall-clock time in this zone, and booking emails display times in it.',
    sort_order: 2,
    default: 'Europe/London',
  },
  // Vertex AI / Google Cloud
  GOOGLE_SA_KEY_BASE64: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    required: true,
  },
  GOOGLE_CLOUD_PROJECT_ID: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    required: true,
  },
  GOOGLE_CLOUD_LOCATION: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    default: 'us-central1',
  },
  // Vertex AI region for Claude text models ('global', 'eu', 'us' or a single region).
  ANTHROPIC_VERTEX_REGION: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    default: 'global',
  },

  // Google Cloud Storage
  GCS_RECORDINGS_BUCKET: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    default: 'carpass-call-recordings',
  },
  GCS_MEDIA_BUCKET: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    default: 'carpass-whatsapp-media',
  },
  GCS_DOCUMENTS_BUCKET: {
    source: ConfigSource.Env,
    type: ConfigType.String,
  },

  // Billing (BigQuery)
  BILLING_PROJECT_ID: { source: ConfigSource.Env, type: ConfigType.String },
  BILLING_PRIVATE_KEY_ID: { source: ConfigSource.Env, type: ConfigType.String },
  BILLING_PRIVATE_KEY: { source: ConfigSource.Env, type: ConfigType.String },
  BILLING_CLIENT_EMAIL: { source: ConfigSource.Env, type: ConfigType.String },
  BILLING_CLIENT_ID: { source: ConfigSource.Env, type: ConfigType.String },
  BILLING_BIGQUERY_DATASET: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    default: 'billing_export',
  },
  BILLING_BIGQUERY_TABLE: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    default: 'gcp_billing_export_v1_*',
  },
  BILLING_BIGQUERY_LOCATION: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    default: 'US',
  },
  BILLING_FILTER_PROJECT_ID: {
    source: ConfigSource.Env,
    type: ConfigType.String,
  },

  // NatWest Payments
  NATWEST_API_KEY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Payments,
    label: 'NatWest API Key',
    description: 'API key for NatWest/Fiserv payment link creation',
    sort_order: 2,
    is_secret: true,
  },
  NATWEST_SHARED_SECRET: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Payments,
    label: 'NatWest Shared Secret',
    description: 'HMAC shared secret for NatWest API authentication',
    sort_order: 3,
    is_secret: true,
  },
  NATWEST_STORE_ID: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Payments,
    label: 'NatWest Store ID',
    description: 'NatWest merchant store identifier',
    sort_order: 4,
  },
  NATWEST_API_URL: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Payments,
    label: 'NatWest API URL',
    description: 'NatWest/Fiserv API base URL',
    sort_order: 5,
    default: 'https://prod.emea.api.fiservapps.com/sandbox/exp/v1',
  },

  // Documenso digital signatures
  DOCUMENSO_API_URL: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.DigitalSign,
    label: 'Documenso API URL',
    description: 'Base URL of the Documenso instance (e.g. https://sign.example.com)',
    sort_order: 1,
  },
  DOCUMENSO_API_TOKEN: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.DigitalSign,
    label: 'Documenso API Token',
    description: 'API token created in Documenso under Settings → API Tokens',
    sort_order: 2,
    is_secret: true,
  },
  DOCUMENSO_WEBHOOK_SECRET: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.DigitalSign,
    label: 'Documenso Webhook Secret',
    description:
      'Secret configured on the Documenso webhook (sent as X-Documenso-Secret). Leave empty to skip verification.',
    sort_order: 3,
    is_secret: true,
  },

  // Stock API
  STOCK_API_URL: { source: ConfigSource.Env, type: ConfigType.String }, //Should be deprecated soon
  STOCK_API_KEY: { source: ConfigSource.Env, type: ConfigType.String }, //Should be deprecated soon
  BLUESKY_API_KEY: { source: ConfigSource.Env, type: ConfigType.String },
  CLIENTS_HANDLERS_API_KEY: { source: ConfigSource.Env, type: ConfigType.String },
  HIPPO_SEARCH_API_KEY: { source: ConfigSource.Env, type: ConfigType.String },
  // Full-access admin key the MCP server sends as `x-api-key` to /mcp/*. Provided at
  // MCP install time; when it matches, every MCP action is allowed.
  MCP_ADMIN_API_KEY: { source: ConfigSource.Env, type: ConfigType.String },
  // Destinations for POST /clients-handlers/save-shifts and /check-matching-shifts.
  // Kept out of the custom_tools rows on purpose: those rows point AT these endpoints,
  // so reading the destination from them would make the handlers post to themselves.
  NEWCROSS_SHIFTS_WEBHOOK_URL: { source: ConfigSource.Env, type: ConfigType.String },
  NEWCROSS_SHIFTS_WEBHOOK_KEY: { source: ConfigSource.Env, type: ConfigType.String },
  NEWCROSS_MATCH_WEBHOOK_URL: { source: ConfigSource.Env, type: ConfigType.String },
  // Shared secret the external Clients Dashboard sends as `x-api-key` to the /public/* endpoints.
  // When set, it is the global master key (grants access to every configuration). When empty,
  // access falls back to each configuration's own `dashboard_api_key`.
  DASHBOARD_API_KEY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.API,
    label: 'Dashboard API Key',
    description:
      'Master key the external Clients Dashboard sends as x-api-key. When set, it grants access to all configurations. Leave empty to use a per-configuration key instead.',
    is_secret: true,
    sort_order: 1,
    default: null,
  },

  // Web Push (VAPID) — for transfer notification extension
  VAPID_PUBLIC_KEY: {
    source: ConfigSource.Env,
    type: ConfigType.String,
  },
  VAPID_PRIVATE_KEY: {
    source: ConfigSource.Env,
    type: ConfigType.String,
  },
  VAPID_SUBJECT: {
    source: ConfigSource.Env,
    type: ConfigType.String,
  },

  CALLS_QUEUE_CONCURRENCY: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.Call,
    label: 'Queue Concurrency',
    description: 'Number of concurrent calls to process in the queue',
    sort_order: 7,
    default: 1,
  },

  // ==================== FEATURE FLAGS ====================
  FEATURE_CHATBOT: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_GCP_COSTS: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_TWILIO_COSTS: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_HUMAN_REVIEW: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_SMS_ACTIONS_MODULE: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_REPORTS: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_WEBHOOKS: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  WEBHOOK_METRIC_ID: {
    source: ConfigSource.Env,
    type: ConfigType.String,
  },
  FEATURE_AUDIO_TIMELINE: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_PAYMENT_LINKS: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_DEALER_KIT: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_STOCK_SYNC: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_WHATSAPP: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_MESSAGING: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_EMAIL: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_BOOKINGS: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    // Live for clients today — default on unless explicitly disabled
    default: true,
    frontend: true,
  },
  FEATURE_FINANCE_APPLICATIONS: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    // Live for clients today — default on unless explicitly disabled
    default: true,
    frontend: true,
  },
  FEATURE_DIGITAL_SIGN: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    // Every client platform ships with e-signatures — default on unless explicitly disabled
    default: true,
    frontend: true,
  },
  FEATURE_COMPARE_KPIS: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_ANALYTICS_REPORTER: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  FEATURE_CLIENTS_VIEW: {
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
    frontend: true,
  },
  HUMAN_REVIEW_DATA_FIELD: {
    source: ConfigSource.Feature,
    type: ConfigType.String,
    default: 'availability_data',
    frontend: true,
  },
  AVAILABILITY_SOURCE_TOOLS: {
    // Comma-separated custom-tool names (e.g. "save_shifts"). When set, the
    // call-detail availability card shows only the args of the last matching
    // tool invocation recorded on the call — never the review agent's
    // extraction. Empty = feature off, card behaves as before.
    source: ConfigSource.Feature,
    type: ConfigType.String,
    default: '',
    frontend: true,
  },
  DISABLED_TOOLS: {
    //should be deprecated soon
    source: ConfigSource.Feature,
    type: ConfigType.String,
    default: '',
  },
  USE_ADK_AGENT: {
    //should be deprecated soon
    source: ConfigSource.Feature,
    type: ConfigType.Boolean,
    default: false,
  },

  // ==================== DB-OVERRIDABLE SETTINGS ====================
  // DealerKit Integration
  DEALERKIT_API_KEY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.DealerKit,
    label: 'DealerKit API Key',
    description: 'Bearer token for DealerKit Integrator API',
    sort_order: 1,
    is_secret: true,
  },
  DEALERKIT_API_BASE_URL: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.DealerKit,
    label: 'DealerKit API Base URL',
    description: 'Base URL for DealerKit Integrator API',
    sort_order: 2,
    default: 'https://api.dealerkit.uk/integrators',
  },
  DEALERKIT_DEALER_ID: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.DealerKit,
    label: 'DealerKit Dealer ID',
    description: 'Dealer ID sent as the dealer_id query param for stock and reservation calls',
    sort_order: 3,
    default: '21992',
  },
  DEALERKIT_STOCK_RESYNC_ENABLED: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.DealerKit,
    label: 'Stock Periodic Re-sync',
    description:
      'When enabled, periodically pull the full stock list to reconcile the local mirror. Off by default — the webhook keeps stock up to date in real time.',
    sort_order: 4,
    default: false,
  },
  DEALERKIT_STOCK_RESYNC_INTERVAL_DAYS: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.DealerKit,
    label: 'Re-sync Interval (days)',
    description:
      'How many days between automatic full stock re-syncs (only used when the periodic re-sync is enabled).',
    sort_order: 5,
    default: 1,
  },
  DEALERKIT_STOCK_RESYNC_TIME: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.DealerKit,
    label: 'Re-sync Time (HH:mm)',
    description: 'Time of day (24h HH:mm) to run the automatic full stock re-sync.',
    sort_order: 6,
    default: '02:00',
  },
  // Branding
  COMPANY_NAME: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Branding,
    label: 'Company Name',
    description: 'Company name displayed in emails and UI',
    sort_order: 1,
    default: 'Calls AI',
  },

  COMPANY_WEBSITE: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Branding,
    label: 'Company Website',
    description: 'Company website URL',
    sort_order: 2,
    default: 'https://example.com',
  },

  COMPANY_LOGO_URL: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Branding,
    label: 'Logo URL (Light Mode)',
    description: 'Company logo URL for light mode',
    sort_order: 3,
    default: '',
  },

  COMPANY_LOGO_DARK: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Branding,
    label: 'Logo URL (Dark Mode)',
    description: 'Company logo URL for dark mode',
    sort_order: 4,
    default: '',
  },

  CLIENT_FAVICON_URL: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Branding,
    label: 'Favicon URL',
    description: 'Favicon URL for the frontend',
    sort_order: 5,
  },

  CLIENT_PRIMARY_COLOR: {
    source: ConfigSource.Setting,
    type: ConfigType.Color,
    category: SettingCategory.Branding,
    label: 'Primary Color',
    description: 'Primary theme color (hex format, e.g., #3B82F6)',
    sort_order: 6,
  },

  CLIENT_PRIMARY_COLOR_DARK: {
    source: ConfigSource.Setting,
    type: ConfigType.Color,
    category: SettingCategory.Branding,
    label: 'Primary Color (Dark)',
    description: 'Primary theme color for dark mode (hex format)',
    sort_order: 7,
  },

  // Email
  EMAIL_PROVIDER: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Email Provider',
    description: "Email provider: 'resend', 'nodemailer', 'sendgrid', or 'azure'",
    sort_order: 1,
  },

  BOOKING_NOTIFICATION_EMAILS: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Booking Notification Emails',
    description:
      'Comma-separated recipients for the "booking made" email. Leave blank to disable booking notifications.',
    sort_order: 7,
    default: '',
  },

  BOOKING_ERROR_EMAILS: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Booking Error Alert Emails',
    description:
      'Comma-separated recipients for booking failure alerts (workflow execution errors). Leave blank to disable.',
    sort_order: 8,
    default: '',
  },

  FINANCE_APPLICATION_EMAILS: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Finance Application Emails',
    description:
      'Comma-separated recipients for the "finance application completed" email. Leave blank to disable finance application notifications.',
    sort_order: 8,
    default: '',
  },

  FINANCE_APPLICATION_EMAIL_TITLE: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Finance Applications / Bookings Email Title',
    description:
      'Company name displayed in finance application and booking emails (e.g. "W18 Cars Ltd"). Defaults to the COMPANY_NAME branding setting if left blank.',
    sort_order: 9,
    default: '',
  },

  RESEND_API_KEY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Resend API Key',
    description: 'API key for Resend email service',
    is_secret: true,
    sort_order: 2,
    default: '',
  },

  RESEND_FROM_EMAIL: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'From Email Address',
    description: 'Sender email address for Resend',
    sort_order: 3,
    default: '',
  },

  SMTP_HOST: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'SMTP Host',
    description: 'SMTP server hostname',
    sort_order: 4,
    default: '',
  },

  SMTP_PORT: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.Email,
    label: 'SMTP Port',
    description: 'SMTP server port',
    sort_order: 5,
    default: 587,
  },

  SMTP_SECURE: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.Email,
    label: 'Use TLS/SSL',
    description: 'Use TLS for SMTP connection',
    sort_order: 6,
    default: false,
  },

  SMTP_USER: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'SMTP Username',
    description: 'SMTP username/email for authentication',
    sort_order: 7,
    default: '',
  },

  SMTP_PASS: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'SMTP Password',
    description: 'SMTP password for authentication',
    is_secret: true,
    sort_order: 8,
    default: '',
  },

  SENDGRID_API_KEY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'SendGrid API Key',
    description: 'API key for SendGrid email service',
    is_secret: true,
    sort_order: 9,
    default: '',
  },

  SENDGRID_FROM_EMAIL: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'SendGrid From Email',
    description: 'Default sender email address for SendGrid',
    sort_order: 10,
  },

  AZURE_TENANT_ID: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Azure Tenant ID',
    description: 'Microsoft Entra (Azure AD) directory/tenant ID',
    sort_order: 11,
    default: '',
  },

  AZURE_CLIENT_ID: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Azure Client ID',
    description: 'Application (client) ID from Azure app registration',
    sort_order: 12,
    default: '',
  },

  AZURE_CLIENT_SECRET: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Azure Client Secret',
    description: 'Client secret value for Azure app registration',
    is_secret: true,
    sort_order: 13,
    default: '',
  },

  AZURE_FROM_EMAIL: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Azure From Email',
    description: 'Microsoft 365 mailbox address to send emails from',
    sort_order: 14,
    default: '',
  },

  AZURE_EMAIL_WEBHOOK_SECRET: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Azure Email Webhook Secret',
    description: 'Client state value used to validate Microsoft Graph webhook notifications',
    is_secret: true,
    sort_order: 16,
    default: '',
  },

  USE_CC_EMAILS: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.Email,
    label: 'Use CC for Bulk Emails',
    description: 'Send bulk emails using CC instead of individual emails',
    sort_order: 17,
    default: false,
  },

  EMAIL_TIMEZONE: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Email Timezone',
    description:
      'Timezone for formatting dates in emails (e.g., Europe/London, America/Los_Angeles)',
    sort_order: 18,
    default: 'Europe/London',
  },

  // --- Conversational email channel (inbound + AI reply) ---
  EMAIL_CHANNEL_PROVIDER: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Email Channel Provider',
    description:
      'Backend for the conversational email channel: "azure" (Microsoft Graph, production) or "imap" (generic IMAP/SMTP, e.g. a Gmail test account).',
    sort_order: 19,
    default: 'azure',
  },

  IMAP_HOST: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'IMAP Host',
    description: 'IMAP server hostname for the email channel (e.g. imap.gmail.com)',
    sort_order: 20,
    default: '',
  },

  IMAP_PORT: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.Email,
    label: 'IMAP Port',
    description: 'IMAP server port (993 for implicit TLS)',
    sort_order: 21,
    default: 993,
  },

  IMAP_SECURE: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.Email,
    label: 'IMAP Use TLS/SSL',
    description: 'Use implicit TLS for the IMAP connection',
    sort_order: 22,
    default: true,
  },

  IMAP_USER: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'IMAP Username',
    description:
      'IMAP/SMTP account username. Also used as the channel from-address for outbound replies.',
    sort_order: 23,
    default: '',
  },

  IMAP_PASSWORD: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'IMAP Password',
    description: 'IMAP/SMTP account password (use an app password for Gmail)',
    is_secret: true,
    sort_order: 24,
    default: '',
  },

  IMAP_SMTP_HOST: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Email,
    label: 'Channel SMTP Host',
    description: 'SMTP host used to send replies for the IMAP channel (e.g. smtp.gmail.com)',
    sort_order: 25,
    default: '',
  },

  IMAP_SMTP_PORT: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.Email,
    label: 'Channel SMTP Port',
    description: 'SMTP port used to send replies for the IMAP channel (587 for STARTTLS)',
    sort_order: 26,
    default: 587,
  },

  IMAP_SMTP_SECURE: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.Email,
    label: 'Channel SMTP Use TLS/SSL',
    description: 'Use implicit TLS for the channel SMTP connection (true for port 465)',
    sort_order: 27,
    default: false,
  },

  // Twilio
  TWILIO_ACCOUNT_SID: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Twilio,
    label: 'Account SID',
    description: 'Twilio Account SID',
    sort_order: 1,
  },

  TWILIO_AUTH_TOKEN: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Twilio,
    label: 'Auth Token (US1)',
    description: 'Twilio Auth Token for US region (default)',
    is_secret: true,
    sort_order: 2,
  },

  TWILIO_AUTH_TOKEN_IE1: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Twilio,
    label: 'Auth Token (IE1)',
    description: 'Twilio Auth Token for EU/Ireland region. Leave empty if not using IE1.',
    is_secret: true,
    sort_order: 3,
  },

  FROM_PHONE_NUMBER: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Twilio,
    label: 'Phone Number',
    description: 'Twilio phone number for outbound calls',
    sort_order: 4,
  },

  TWILIO_SMS_AUTO_REPLY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Twilio,
    label: 'SMS Auto Reply',
    description: 'Automatic reply message for incoming SMS',
    sort_order: 5,
    default: 'Thank you for your message. We will get back to you soon.',
  },

  TWILIO_DEFAULT_BUY_COUNTRY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Twilio,
    label: 'Default Buy Country',
    description: 'Default ISO-2 country code for number search and purchase',
    sort_order: 6,
    default: 'GB',
  },

  TWILIO_BUNDLE_SID: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Twilio,
    label: 'Regulatory Bundle SID',
    description:
      'Approved regulatory bundle (BU...) to attach when buying numbers. Only needed when the account has more than one approved bundle for the country.',
    sort_order: 7,
  },

  TWILIO_ADDRESS_SID: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Twilio,
    label: 'Address SID',
    description:
      'Address (AD...) to attach when buying numbers. Only needed when the account has more than one address for the country.',
    sort_order: 8,
  },

  // Telnyx
  TELNYX_API_KEY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Telnyx,
    label: 'API Key',
    description: 'Telnyx API Key (Bearer token)',
    is_secret: true,
    sort_order: 1,
  },

  TELNYX_CONNECTION_ID: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Telnyx,
    label: 'Connection ID',
    description: 'Telnyx SIP Connection ID for outbound calls',
    sort_order: 2,
  },

  TELNYX_PHONE_NUMBER: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Telnyx,
    label: 'Phone Number',
    description: 'Telnyx phone number for outbound calls',
    sort_order: 3,
  },

  TELNYX_PUBLIC_KEY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Telnyx,
    label: 'Webhook Public Key',
    description:
      'Telnyx Ed25519 public key (base64) used to verify webhook signatures. Find it in the Telnyx portal under Mission Control > Webhooks.',
    is_secret: true,
    sort_order: 4,
  },

  TELNYX_MESSAGING_PROFILE_ID: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Telnyx,
    label: 'Messaging Profile ID',
    description:
      'Telnyx Messaging Profile ID for outbound SMS. The same profile is auto-attached to inbound DIDs so incoming texts route to the backend webhook.',
    sort_order: 6,
  },

  TELNYX_SMS_SENDER_ID: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Telnyx,
    label: 'SMS Sender ID',
    description:
      'Alphanumeric sender ID (up to 11 characters, e.g. "V12") used as the From value for outbound SMS. Useful when your Telnyx DID does not support SMS (e.g. UK landlines). When set, takes precedence over TELNYX_PHONE_NUMBER for messaging only. SMS is one-way — recipients cannot reply.',
    sort_order: 7,
  },

  TELNYX_WEBHOOK_TIMESTAMP_TOLERANCE_S: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.Telnyx,
    label: 'Webhook Timestamp Tolerance (s)',
    description:
      'Maximum age (seconds) of a Telnyx webhook before it is rejected as stale. Default 300.',
    sort_order: 5,
    default: 300,
  },

  // WhatsApp
  WHATSAPP_ACCESS_TOKEN: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.WhatsApp,
    label: 'Access Token',
    description:
      'Meta permanent system user access token with whatsapp_business_messaging permission',
    is_secret: true,
    sort_order: 1,
  },
  WHATSAPP_APP_SECRET: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.WhatsApp,
    label: 'App Secret',
    description: 'Meta App Secret used to verify webhook signatures (X-Hub-Signature-256)',
    is_secret: true,
    sort_order: 2,
  },
  WHATSAPP_VERIFY_TOKEN: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.WhatsApp,
    label: 'Webhook Verify Token',
    description: 'Token used during initial webhook URL verification by Meta',
    sort_order: 3,
  },
  WHATSAPP_BUSINESS_ACCOUNT_ID: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.WhatsApp,
    label: 'Business Account ID',
    description: 'WhatsApp Business Account ID from Meta Business Suite',
    sort_order: 4,
  },
  WHATSAPP_API_VERSION: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.WhatsApp,
    label: 'Graph API Version',
    description: 'Meta Graph API version (e.g., v21.0)',
    sort_order: 5,
    default: 'v21.0',
  },
  WHATSAPP_APP_ID: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.WhatsApp,
    label: 'App ID',
    description: 'Meta App ID for WhatsApp (used for image uploads in templates)',
    sort_order: 6,
  },

  WHATSAPP_BATCH_WINDOW_MS: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.WhatsApp,
    label: 'Message Batch Window (ms)',
    description:
      'How long to wait after an inbound message before answering, so someone typing across ' +
      'several messages gets one reply instead of one per line. The wait restarts on each new ' +
      'message, so it is time since the last one, not since the first. 0 answers immediately.',
    sort_order: 7,
    default: 2000,
  },

  WHATSAPP_BLOCKED_NUMBERS: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.WhatsApp,
    label: 'Blocked Numbers',
    description:
      'Phone numbers the WhatsApp agent ignores, in international format (e.g. +44 7514 473124), ' +
      'separated by commas or new lines. Their messages are dropped and their calls are rejected. ' +
      'Takes effect within a minute, no redeploy needed.',
    sort_order: 8,
  },

  WHATSAPP_PHONE_REQUEST_TEXT: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.WhatsApp,
    label: 'Phone Number Request Text',
    description:
      'Customers with a WhatsApp username can message without sharing their phone number. After ' +
      'the agent\'s first reply to one, WhatsApp\'s "share phone number" button is sent once with this ' +
      'text, so their number is saved to their contact. Leave empty to never ask.',
    sort_order: 9,
    default: 'So we can keep you updated, please share your phone number with the button below.',
  },

  // Call Settings
  VOICE_ACCENT: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Call,
    label: 'Voice Accent',
    description: 'Voice accent for the AI (e.g., British, American, Australian)',
    sort_order: 1,
  },

  WELCOME_GRACE_PERIOD_MS: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.Call,
    label: 'Welcome Grace Period (ms)',
    description:
      'Grace period (in ms) before allowing user to interrupt welcome message (Welcome Interruptible must be enabled)',
    sort_order: 2,
    default: 1500,
  },

  WELCOME_INTERRUPTIBLE: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.Call,
    label: 'Welcome Interruptible',
    description: 'Whether the welcome message can be interrupted by user speech',
    sort_order: 3,
    default: true,
  },

  AUTO_VALIDATE_CALLS: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.Call,
    label: 'Auto-Validate Calls',
    description: 'Enable automatic validation of calls after they end',
    sort_order: 4,
    default: false,
  },

  SEND_SMS_WITH_AVAILABILITY_CONFIRMATION: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.Call,
    label: 'Send SMS Availability Confirmation',
    description: 'Send SMS confirmation when availability is captured',
    sort_order: 5,
    default: false,
  },

  WELCOME_VOICE_ACCENT_CODE: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Call,
    label: 'Welcome Voice Accent Code',
    description: 'Language code for welcome message voice accent (e.g., en-GB, en-US)',
    sort_order: 6,
    default: 'en-GB',
  },

  // API Settings
  OPENAI_API_KEY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.API,
    label: 'OpenAI API Key',
    description: 'API key for OpenAI services',
    is_secret: true,
    sort_order: 1,
  },

  ANTHROPIC_API_KEY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.API,
    label: 'Anthropic API Key',
    description:
      'API key for Anthropic Claude — used by the AI Analyst when a report selects the Anthropic provider',
    is_secret: true,
    sort_order: 2,
  },

  GEMINI_API_KEY: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    default: '',
  },

  GOOGLE_MAPS_API_KEY: {
    source: ConfigSource.Env,
    type: ConfigType.String,
    default: '',
  },

  // ==================== VOICE AGENT SETTINGS ====================

  // VAD
  VA_VAD_ENABLED: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.VoiceAgent,
    label: 'Enable Automatic VAD',
    description: 'Enable automatic voice activity detection',
    sort_order: 1,
    default: true,
  },
  VA_VAD_START_SENSITIVITY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.VoiceAgent,
    label: 'Start-of-Speech Sensitivity',
    description: 'How sensitive the model is to detecting speech start (LOW, HIGH)',
    sort_order: 2,
    default: 'LOW',
  },
  VA_VAD_END_SENSITIVITY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.VoiceAgent,
    label: 'End-of-Speech Sensitivity',
    description: 'How sensitive the model is to detecting speech end (LOW, HIGH)',
    sort_order: 3,
    default: 'LOW',
  },
  VA_VAD_PREFIX_PADDING_MS: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.VoiceAgent,
    label: 'Prefix Padding (ms)',
    description: 'Milliseconds of audio to include before detected speech start',
    sort_order: 4,
    default: 250,
  },
  VA_VAD_SILENCE_DURATION_MS: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.VoiceAgent,
    label: 'Silence Duration (ms)',
    description: 'Milliseconds of silence before speech is considered ended',
    sort_order: 5,
    default: 550,
  },

  // Thinking
  VA_THINKING_ENABLED: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.VoiceAgent,
    label: 'Enable Thinking',
    description: 'Enable dynamic thinking (on by default for supported models)',
    sort_order: 6,
    default: true,
  },
  VA_THINKING_BUDGET: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.VoiceAgent,
    label: 'Thinking Budget',
    description: 'Maximum tokens for thinking process',
    sort_order: 7,
    default: 512,
  },
  VA_THINKING_INCLUDE_THOUGHTS: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.VoiceAgent,
    label: 'Include Thought Summaries',
    description: 'Include thought summaries in the output',
    sort_order: 8,
    default: false,
  },

  // Transcription
  VA_INPUT_TRANSCRIPTION: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.VoiceAgent,
    label: 'Input Audio Transcription',
    description: 'Transcribe user speech to text',
    sort_order: 9,
    default: true,
  },
  VA_OUTPUT_TRANSCRIPTION: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.VoiceAgent,
    label: 'Output Audio Transcription',
    description: 'Transcribe agent speech to text',
    sort_order: 10,
    default: true,
  },

  // Native Audio Features
  VA_AFFECTIVE_DIALOG: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.VoiceAgent,
    label: 'Affective Dialog',
    description: 'Enable emotion-aware responses (native audio models only)',
    sort_order: 11,
    default: true,
  },
  VA_PROACTIVE_AUDIO: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.VoiceAgent,
    label: 'Proactive Audio',
    description: 'Let the model decide when to respond (native audio models only)',
    sort_order: 12,
    default: false,
  },

  // Context Window Compression
  VA_CONTEXT_COMPRESSION_ENABLED: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.VoiceAgent,
    label: 'Enable Context Compression',
    description: 'Compress context window when it gets too large',
    sort_order: 13,
    default: true,
  },
  VA_CONTEXT_TRIGGER_TOKENS: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.VoiceAgent,
    label: 'Trigger Tokens',
    description: 'Compression kicks in at this token count',
    sort_order: 14,
    default: 100000,
  },
  VA_CONTEXT_TARGET_TOKENS: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.VoiceAgent,
    label: 'Target Tokens (sliding window)',
    description: 'Tokens to keep after compression',
    sort_order: 15,
    default: 80000,
  },

  VA_MAX_AGENT_HANDOFFS: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.VoiceAgent,
    label: 'Max Multi-Agent Handoffs Per Call',
    description:
      'Cap on transfer_to_agent calls per live session. Prevents ping-pong loops. Set higher for flows with many specialised agents (4+); lower for tighter flows.',
    sort_order: 16,
    default: 15,
  },

  // ==================== PAYMENT SETTINGS ====================

  RESERVATION_FEE_AMOUNT: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.Payments,
    label: 'Reservation Fee Amount (GBP)',
    description: 'Refundable reservation deposit amount sent via payment link',
    sort_order: 1,
    default: 199,
  },
  PAYMENT_NOTIFICATION_EMAILS: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Payments,
    label: 'Payment Notification Emails',
    description: 'Comma-separated email addresses to notify when a payment is received',
    sort_order: 6,
  },
  CREATE_DEALERKIT_RESERVATION_ON_PAYMENT: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.Payments,
    label: 'Create DealerKit Reservation on Payment',
    description:
      'When enabled, a successful payment automatically creates a reservation in DealerKit (matched by vehicle registration). Requires the DealerKit feature and a synced stock mirror.',
    sort_order: 7,
    default: false,
  },
  DEALERKIT_RESERVATION_ADDRESS_LINE_1: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Payments,
    label: 'Reservation Address — Line 1',
    description:
      'Default address line 1 sent with DealerKit reservations (placeholder until real customer addresses are captured).',
    sort_order: 8,
    default: '1 High Street',
  },
  DEALERKIT_RESERVATION_TOWN: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Payments,
    label: 'Reservation Address — Town/City',
    description: 'Default town/city sent with DealerKit reservations.',
    sort_order: 9,
    default: 'London',
  },
  DEALERKIT_RESERVATION_COUNTY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Payments,
    label: 'Reservation Address — County',
    description: 'Default county sent with DealerKit reservations (required by DealerKit).',
    sort_order: 10,
    default: 'Greater London',
  },
  DEALERKIT_RESERVATION_POST_CODE: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Payments,
    label: 'Reservation Address — Post Code',
    description: 'Default post code sent with DealerKit reservations.',
    sort_order: 11,
    default: 'SW1A 1AA',
  },
  DEALERKIT_RESERVATION_COUNTRY: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.Payments,
    label: 'Reservation Address — Country',
    description: 'Default country sent with DealerKit reservations (max 2 characters, e.g. UK).',
    sort_order: 12,
    default: 'UK',
  },

  // ==================== CAMPAIGNS ====================
  // Defaults applied to NEW campaigns at create time (snapshot). Editing these
  // does not retroactively change existing campaigns — each campaign keeps its
  // own editable copy.
  CAMPAIGN_DEFAULT_RETRYABLE_OUTCOMES: {
    source: ConfigSource.Setting,
    type: ConfigType.String, // CSV of CallOutcome values
    category: SettingCategory.Campaigns,
    label: 'Default Retryable Outcomes',
    description:
      'Comma-separated call outcomes that trigger a retry when a campaign call ends with them. ' +
      'Applied as the default for new campaigns; editable per campaign.',
    sort_order: 1,
    default: 'NO_ANSWER,BUSY,VOICEMAIL,CANCELED,FAILED',
  },
  CAMPAIGN_DEFAULT_OUTCOME_SOURCE: {
    source: ConfigSource.Setting,
    type: ConfigType.String, // 'RAW' | 'REVIEWED'
    category: SettingCategory.Campaigns,
    label: 'Default Outcome Source',
    description:
      'Which outcome the retry logic trusts for new campaigns: REVIEWED (post-call review ' +
      'verdict via calls_with_review — recommended) or RAW (the outcome recorded at call-end). ' +
      'Editable per campaign.',
    sort_order: 2,
    default: 'REVIEWED',
  },

  // ==================== SALES CONSOLE / MESSAGING ====================
  CONTACT_TAG_DEFINITIONS: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.SalesConsole,
    label: 'Contact Tags — Types & Colors',
    description:
      'Tag types for contacts. Each type (e.g. source) has one color shared by all of its tags ' +
      '(e.g. dealerkit, autoconvert, website). A contact holds at most one tag per type — ' +
      'adding another tag of the type replaces the current one.',
    sort_order: 1,
    default: '[]',
  },
  SALES_CONSOLE_DEFAULT_FILTERS: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.SalesConsole,
    label: 'Default Sales Console Filters',
    description:
      'Starting filters for the Sales Console list. Applied only for users who have never ' +
      'changed their filters — after that, each user’s own choices (saved in their browser) win.',
    sort_order: 2,
    default: '',
  },
  MESSAGING_TAKEOVER_MESSAGE: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.SalesConsole,
    label: 'Operator Takeover Message',
    description:
      'Message sent to the customer when a human operator takes over a conversation. ' +
      'Use {{user_name}} for the operator’s name. Leave empty to use the default ' +
      '"{{user_name}} joined the conversation".',
    sort_order: 3,
    default: '{{user_name}} joined the conversation',
  },
  MESSAGING_HANDBACK_MESSAGE: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.SalesConsole,
    label: 'Operator Handback Message',
    description:
      'Message sent to the customer when the conversation is handed back to the AI. ' +
      'Use {{user_name}} for the operator’s name. Leave empty to send no message (default).',
    sort_order: 4,
  },
  CONTACTS_SHOW_TOOL_CALLS: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.SalesConsole,
    label: 'Show Tool Calls in Conversations',
    description:
      'When enabled, the contacts conversation view also shows agent tool-call events ' +
      '(tool name and arguments) inline with the messages.',
    sort_order: 5,
    default: false,
  },
  AI_CLOSE_QUIET_HOURS: {
    source: ConfigSource.Setting,
    type: ConfigType.Number,
    category: SettingCategory.SalesConsole,
    label: 'Quiet hours after the AI closes a chat',
    description:
      'After the agent ends a WhatsApp conversation (end_call), a bare "thanks"/"ok"/emoji ' +
      'within this many hours gets no AI reply. Any other message reopens the chat.',
    sort_order: 6,
    default: 12,
  },
  MESSAGING_OPT_OUT_MESSAGE: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.SalesConsole,
    label: 'Opt-out acknowledgement',
    description:
      'Sent once when a customer replies STOP / "don\'t message me". Leave empty for ' +
      '"Understood — we won\'t message you again."',
    sort_order: 7,
    default: '',
  },
  SALES_CONSOLE_INTENT_LABEL: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.SalesConsole,
    label: 'High-intent filter — button label',
    description:
      'Name of the Sales Console filter button that shows glowing chats nobody has cleared yet ' +
      '(a watched tool fired — turn Watch on per tool on the Custom Tools page). Leave empty ' +
      'for "High intent".',
    sort_order: 8,
    default: 'High intent',
  },
  SALES_CONSOLE_INTENT_ICON: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.SalesConsole,
    label: 'High-intent filter — icon',
    description:
      'Emoji shown on the high-intent filter button (e.g. 🔥, ⭐, 💰, 🎯). Leave empty for no icon.',
    sort_order: 10,
    default: '🔥',
  },
  SALES_CONSOLE_STAFF_MESSAGE_COLOR: {
    source: ConfigSource.Setting,
    type: ConfigType.Color,
    category: SettingCategory.SalesConsole,
    label: 'Staff message colour',
    description:
      'Bubble colour for messages a team member sent from the Sales Console, so they stand ' +
      'out from AI replies. Leave empty for the default green.',
    sort_order: 11,
    default: '#047857',
  },

  // ==================== ANALYTICS REFACTOR ====================
  ANALYTICS_V2_ENABLED: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.Features,
    label: 'Analytics V2 Enabled',
    description:
      'When true, the analytics service reads metric_definitions from DB and uses the new DSL-driven path. When false, falls back to the legacy metric-registry. Toggle off to revert reads without redeploy. Wired up in Phase 3 of the analytics refactor.',
    sort_order: 1,
    default: false,
  },
  AGENT_IMPROVEMENT_ENABLED: {
    source: ConfigSource.Setting,
    type: ConfigType.Boolean,
    category: SettingCategory.AgentImprovement,
    label: 'Agent Improvement',
    description:
      'Turns the agent improvement harness on or off at runtime: judge tuning, the daily verify/patch/replay pipeline and its endpoints. Which configurations it works on is chosen per configuration (autonomy level).',
    sort_order: 1,
    default: false,
    frontend: true,
  },
  AGENT_IMPROVEMENT_SIMULATOR_MODEL: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.AgentImprovement,
    label: 'Simulated customer model',
    description: 'Gemini model that plays the customer in replays.',
    sort_order: 2,
    default: 'gemini-2.5-flash',
  },
  AGENT_IMPROVEMENT_VOICE_CUSTOMER_MODE: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.AgentImprovement,
    label: 'Voice replay customer',
    description:
      'live = the customer is its own Gemini Live voice session; tts = text model + Google TTS',
    sort_order: 3,
    default: 'live',
  },
  AGENT_IMPROVEMENT_CUSTOMER_LIVE_MODEL: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.AgentImprovement,
    label: 'Customer live model',
    description: 'Gemini Live model used for the customer voice session.',
    sort_order: 4,
    default: 'gemini-live-2.5-flash-native-audio',
  },
  AGENT_IMPROVEMENT_TEST_PHONE: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.AgentImprovement,
    label: 'Test phone number',
    description:
      'E.164 number cloned test contacts carry during replays; when set, message tools send during a replay go here for real.',
    sort_order: 5,
  },
  AGENT_IMPROVEMENT_TEST_EMAIL: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.AgentImprovement,
    label: 'Test email address',
    description: 'Mailbox cloned test contacts carry during replays.',
    sort_order: 6,
  },
  AGENT_IMPROVEMENT_SIMULATE_SIDE_EFFECTS: {
    source: ConfigSource.Setting,
    type: ConfigType.String,
    category: SettingCategory.AgentImprovement,
    label: 'Side-effect simulation mode',
    description:
      'none = every tool runs for real; customer_messages = simulate only customer-facing message sends when the contact has a synthetic identity; all = simulate all side-effecting tools (legacy).',
    sort_order: 7,
    default: 'customer_messages',
  },
} as const;

// ==================== TYPE INFERENCE ====================

export type ConfigKey = keyof typeof CONFIG_REGISTRY;

/**
 * Infer the return type based on the config entry
 */
export type ConfigReturnType<K extends ConfigKey> = (typeof CONFIG_REGISTRY)[K] extends {
  type: ConfigType.String;
}
  ? (typeof CONFIG_REGISTRY)[K] extends { required: true } | { default: string }
    ? string
    : string | null
  : (typeof CONFIG_REGISTRY)[K] extends { type: ConfigType.Number }
    ? (typeof CONFIG_REGISTRY)[K] extends { required: true } | { default: number }
      ? number
      : number | null
    : (typeof CONFIG_REGISTRY)[K] extends { type: ConfigType.Boolean }
      ? (typeof CONFIG_REGISTRY)[K] extends { required: true } | { default: boolean }
        ? boolean
        : boolean | null
      : never;

/**
 * Helper to get all keys of a specific source
 */
export function getKeysBySource(source: ConfigSource): ConfigKey[] {
  return (Object.keys(CONFIG_REGISTRY) as ConfigKey[]).filter(
    (key) => CONFIG_REGISTRY[key].source === source,
  );
}

/**
 * Helper to get all setting keys for auto-seeding
 */
export function getSettingKeys(): ConfigKey[] {
  return getKeysBySource(ConfigSource.Setting);
}

/**
 * Keys whose source is Feature
 */
export type FeatureKey = {
  [K in ConfigKey]: (typeof CONFIG_REGISTRY)[K]['source'] extends ConfigSource.Feature ? K : never;
}[ConfigKey];

/**
 * Helper to get feature keys exposed to the frontend
 */
export function getFrontendFeatureKeys(): ConfigKey[] {
  return (Object.keys(CONFIG_REGISTRY) as ConfigKey[]).filter((key) => {
    const entry = CONFIG_REGISTRY[key];
    // Never expose secret settings to the frontend.
    if ('is_secret' in entry && entry.is_secret) return false;
    return (
      (entry.source === ConfigSource.Feature || entry.source === ConfigSource.Setting) &&
      'frontend' in entry &&
      entry.frontend === true
    );
  });
}
